// src/components/Services.jsx (Centered Categories & Dynamic Time)
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BookingModal from './BookingModal';

import { API_BASE } from '../../config/runtime';

const Services = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchCategories, setBranchCategories] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [branchCategoriesLoading, setBranchCategoriesLoading] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);
  const [branchError, setBranchError] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showSelectedPanel, setShowSelectedPanel] = useState(false);
  const scrollRefs = useRef({});
  const isScrolling = useRef(false);

  const user = JSON.parse(localStorage.getItem('user')) || null;
  const isLoggedIn = user && user.role?.toLowerCase() === 'client';
  const branchSelected = Boolean(selectedBranchId);

  const totalPrice = selectedServices.reduce((sum, s) => sum + Number(s.price), 0);

  const handleToggleService = (service, e) => {
    if (e && e.target.closest('.view-btn')) return;
    if (!selectionMode || !branchSelected) return;
    if (selectedServices.find(s => s.id === service.id)) {
      setSelectedServices(selectedServices.filter(s => s.id !== service.id));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };

  const handleViewService = (service, e) => {
    e.stopPropagation();
    setSelectedService(service);
  };

  const handleToggleSelectionMode = () => {
    if (selectionMode) {
      setSelectionMode(false);
      setSelectedServices([]);
      setSelectedBranchId('');
      setBranchError('');
      setBranchCategories([]);
      setShowSelectedPanel(false);
    } else {
      setSelectionMode(true);
    }
  };

  const handleBookClick = () => {
    if (selectedServices.length > 0) setShowBookingModal(true);
  };

  const handleBookingComplete = () => {
    setSelectedServices([]);
    setSelectionMode(false);
    setSelectedBranchId('');
    setBranchCategories([]);
    setShowSelectedPanel(false);
  };

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/get_services.php`);
        const data = await response.json();
        if (data.success) setServices(data.services || []);
      } catch {
        setServices([]);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    if (!selectionMode) return;
    const fetchBranches = async () => {
      setBranchLoading(true);
      setBranchError('');
      try {
        const response = await fetch(`${API_BASE}/get_branches.php`);
        const data = await response.json();
        if (data.success) {
          setBranches(data.branches || []);
        } else {
          setBranches([]);
          setBranchError(data.message || 'Unable to load branches.');
        }
      } catch {
        setBranches([]);
        setBranchError('Unable to load branches.');
      } finally {
        setBranchLoading(false);
      }
    };
    fetchBranches();
  }, [selectionMode]);

  useEffect(() => {
    if (!selectedBranchId) {
      setBranchCategories([]);
      setBranchCategoriesLoading(false);
      setSelectedServices([]);
      return;
    }
    const fetchBranchCategories = async () => {
      setBranchCategoriesLoading(true);
      try {
        const response = await fetch(
          `${API_BASE}/get_branch_categories.php?branch_id=${selectedBranchId}`
        );
        const data = await response.json();
        if (data.success) {
          setBranchCategories(data.categories || []);
        } else {
          setBranchCategories([]);
        }
      } catch {
        setBranchCategories([]);
      } finally {
        setBranchCategoriesLoading(false);
      }
    };
    fetchBranchCategories();
    setSelectedServices([]);
  }, [selectedBranchId]);

  useEffect(() => {
    const autoScroll = setInterval(() => {
      if (isScrolling.current) return;
      Object.keys(scrollRefs.current).forEach((category) => {
        const el = scrollRefs.current[category];
        if (el) {
          const maxScroll = el.scrollWidth - el.clientWidth;
          if (el.scrollLeft >= maxScroll - 10) {
            el.scrollLeft = 0;
          } else {
            el.scrollLeft += 1;
          }
        }
      });
    }, 30);
    return () => clearInterval(autoScroll);
  }, []);

  const formatDuration = (totalMinutes) => {
    if (totalMinutes === null || totalMinutes === undefined || totalMinutes === 0) return null;
    if (totalMinutes < 60) return `${totalMinutes} mins`;
    const hours = totalMinutes / 60;
    const displayHours = hours % 1 === 0 ? hours : hours.toFixed(1);
    return `${displayHours} ${hours === 1 ? 'hr' : 'hrs'}`;
  };

  const filteredServices = useMemo(() => {
    if (!branchSelected) return services;
    if (!services.length) return [];
    if (branchCategoriesLoading) return [];
    const selectedId = Number(selectedBranchId);
    const branchCategoryIds = new Set(branchCategories.map((c) => Number(c.category_id)));
    return services.filter((service) => {
      const branchIds = Array.isArray(service.branch_ids) ? service.branch_ids : [];
      const branchOk = branchIds.length === 0 || branchIds.some((id) => Number(id) === selectedId);
      if (!branchOk) return false;
      if (branchCategoryIds.size === 0) return false;
      return branchCategoryIds.has(Number(service.category_id));
    });
  }, [branchSelected, services, selectedBranchId, branchCategories, branchCategoriesLoading]);

  const visibleServices = useMemo(() => {
    if (!selectionMode) return services;
    if (!branchSelected) return [];
    return filteredServices;
  }, [selectionMode, branchSelected, filteredServices, services]);

  const groupedServices = useMemo(() => {
    return visibleServices.reduce((acc, service) => {
      const cat = service.category_name || 'General Services';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(service);
      return acc;
    }, {});
  }, [visibleServices]);

  useEffect(() => {
    if (!selectionMode || !branchSelected) return;
    const allowedIds = new Set(filteredServices.map((service) => service.id));
    const nextSelected = selectedServices.filter((service) => allowedIds.has(service.id));
    if (nextSelected.length !== selectedServices.length) setSelectedServices(nextSelected);
  }, [selectionMode, branchSelected, filteredServices]);

  const getInfiniteServices = (category) => {
    const items = groupedServices[category] || [];
    if (items.length <= 4) return items;
    return [...items, ...items, ...items];
  };

  const SCROLL_AMOUNT = typeof window !== 'undefined' && window.innerWidth < 640 ? 220 : 618;

  const scrollLeft = (category) => {
    isScrolling.current = true;
    if (scrollRefs.current[category]) {
      scrollRefs.current[category].scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
    }
    setTimeout(() => { isScrolling.current = false; }, 1000);
  };

  const scrollRight = (category) => {
    isScrolling.current = true;
    if (scrollRefs.current[category]) {
      scrollRefs.current[category].scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
    }
    setTimeout(() => { isScrolling.current = false; }, 1000);
  };

  return (
    <div className="py-8 sm:py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-10 sm:mb-16 border-b border-gray-50 pb-6 sm:pb-8">
          <h2 className="text-3xl sm:text-5xl font-serif uppercase tracking-tighter text-gray-900">Services</h2>
          <div className="flex gap-2 sm:gap-3">
            {selectionMode && (
              <button
                className={`flex-1 sm:flex-none px-5 sm:px-8 py-2.5 sm:py-3 rounded-full text-[10px] font-black uppercase tracking-[0.2em] transition-all ${
                  selectedServices.length > 0
                    ? 'bg-[#984e5c] text-white hover:bg-black'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                onClick={handleBookClick}
                disabled={selectedServices.length === 0}
              >
                {selectedServices.length > 0 ? `Book (${selectedServices.length})` : 'Book'}
              </button>
            )}
            <button
              className={`flex-1 sm:flex-none px-5 sm:px-8 py-2.5 sm:py-3 rounded-full text-[10px] font-black uppercase tracking-[0.2em] transition-all ${
                selectionMode
                  ? 'bg-[#984e5c] text-white hover:bg-black'
                  : 'bg-black text-white hover:bg-[#984e5c]'
              }`}
              onClick={handleToggleSelectionMode}
            >
              {selectionMode ? 'Cancel' : 'Select Services'}
            </button>
          </div>
        </div>

        {/* Branch Selector */}
        {selectionMode && (
          <div className="mb-8 sm:mb-12 rounded-2xl sm:rounded-3xl border border-gray-100 bg-gray-50 p-4 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
              <div>
                <p className="text-xs font-black text-gray-400 uppercase tracking-[0.3em]">Select Branch</p>
                <p className="mt-1 text-xs text-gray-500">Choose a branch to view its available services.</p>
              </div>
              <div className="w-full sm:min-w-[240px] sm:flex-1 sm:max-w-xs">
                <select
                  value={selectedBranchId}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  className="w-full rounded-full border border-gray-200 bg-white px-4 py-3 text-xs font-black uppercase tracking-[0.2em] text-gray-700 focus:border-[#984e5c] focus:outline-none focus:ring-2 focus:ring-[#984e5c]/20 appearance-none"
                >
                  <option value="">Choose a branch</option>
                  {branches.map((branch) => (
                    <option key={branch.branch_id} value={branch.branch_id}>
                      {branch.branch_name}{branch.address ? ` - ${branch.address}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {branchLoading && <p className="mt-3 text-xs text-gray-400">Loading branches...</p>}
            {!branchLoading && branchError && <p className="mt-3 text-xs text-red-500">{branchError}</p>}
            {!branchLoading && !branchError && branches.length === 0 && (
              <p className="mt-3 text-xs text-gray-400">No branches available.</p>
            )}
          </div>
        )}

        {/* Selected Services Panel — collapsible on mobile */}
        {selectionMode && selectedServices.length > 0 && (
          <div className="mb-8 sm:mb-12 bg-gray-50 rounded-2xl sm:rounded-3xl overflow-hidden">
            {/* Toggle header */}
            <button
              className="w-full flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4"
              onClick={() => setShowSelectedPanel(prev => !prev)}
            >
              <span className="text-xs font-black text-gray-400 uppercase tracking-[0.3em]">
                Selected ({selectedServices.length}) —&nbsp;
                <span className="text-[#984e5c]">&#8369;{totalPrice.toLocaleString()}</span>
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedServices([]); }}
                  className="text-[10px] font-bold text-gray-400 hover:text-red-500 uppercase tracking-wider"
                >
                  Clear All
                </button>
                <span className={`sm:hidden text-gray-400 text-xs transition-transform duration-200 ${showSelectedPanel ? 'rotate-180' : ''}`}>▾</span>
              </div>
            </button>

            <div className={`${showSelectedPanel ? 'block' : 'hidden'} sm:block border-t border-gray-100 px-4 sm:px-6 pb-4 sm:pb-6 pt-3 sm:pt-4`}>
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-4">
                {selectedServices.map((service) => (
                  <div
                    key={service.id}
                    className="flex items-center gap-2 sm:gap-3 bg-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-sm border border-gray-100"
                  >
                    <span className="text-[10px] font-bold text-gray-800 uppercase truncate max-w-[120px] sm:max-w-[150px]">
                      {service.name}
                    </span>
                    <span className="text-[10px] font-black text-[#984e5c]">
                      &#8369;{Number(service.price).toLocaleString()}
                    </span>
                    <button
                      onClick={() => setSelectedServices(selectedServices.filter(s => s.id !== service.id))}
                      className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 hover:bg-red-100 hover:text-red-500 transition-all text-xs"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="pt-3 sm:pt-4 border-t border-gray-200 flex justify-between items-center">
                <span className="text-xs font-black text-gray-400 uppercase tracking-wider">Total</span>
                <span className="text-xl sm:text-2xl font-black text-[#984e5c]">
                  &#8369;{totalPrice.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Services Grid */}
        {loading ? (
          <div className="text-center text-sm text-gray-400 py-10">Loading services...</div>
        ) : selectionMode && !branchSelected ? (
          <div className="rounded-2xl sm:rounded-3xl border border-dashed border-gray-200 bg-gray-50 p-8 sm:p-10 text-center text-sm text-gray-500">
            Select a branch to view available services.
          </div>
        ) : selectionMode && branchSelected && branchCategoriesLoading ? (
          <div className="text-center text-sm text-gray-400 py-10">Loading services for this branch...</div>
        ) : Object.keys(groupedServices).length === 0 ? (
          <div className="rounded-2xl sm:rounded-3xl border border-dashed border-gray-200 bg-gray-50 p-8 sm:p-10 text-center text-sm text-gray-500">
            {selectionMode && branchSelected ? 'No services available for this branch.' : 'No services available.'}
          </div>
        ) : (
          Object.keys(groupedServices).map((category) => (
            <div key={category} className="mb-10 sm:mb-16">
              {/* Category header */}
              <div className="flex items-center justify-center mb-6 sm:mb-10">
                <div className="flex-1 h-px bg-gray-100"></div>
                <h3 className="px-4 sm:px-6 text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-[0.3em] sm:tracking-[0.4em] text-center whitespace-nowrap">
                  {category}
                </h3>
                <div className="flex-1 h-px bg-gray-100"></div>
              </div>

              {/* Scrollable row */}
              <div className="relative">
                <div className="flex items-center">
                  {/* Left arrow — smaller on mobile */}
                  <button
                    onClick={() => scrollLeft(category)}
                    className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-100 mr-2 sm:mr-4 text-sm"
                  >
                    &#x2190;
                  </button>

                  <div
                    ref={(el) => (scrollRefs.current[category] = el)}
                    className="flex-1 flex gap-3 sm:gap-6 pb-4"
                    style={{ overflow: 'hidden' }}
                  >
                    {getInfiniteServices(category).map((service, idx) => {
                      const isSelected = Boolean(selectedServices.find(s => s.id === service.id));
                      return (
                        <div
                          key={`${service.id}-${idx}`}
                          className={`group relative flex flex-col items-center flex-shrink-0 transition-all duration-300
                            w-[160px] sm:w-[200px]
                            ${selectionMode && isSelected ? 'ring-4 ring-[#984e5c] rounded-3xl scale-95' : ''}
                          `}
                          onClick={(e) => handleToggleService(service, e)}
                        >
                          <div className="relative w-full aspect-[3/4] mb-3 sm:mb-4 overflow-hidden rounded-t-full bg-gray-50 group-hover:shadow-xl transition-all duration-500">
                            {service.image ? (
                              <img
                                src={service.image}
                                alt=""
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[9px] text-gray-300 uppercase italic">
                                Hygea
                              </div>
                            )}

                            {/* View button */}
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[2px]">
                              <button
                                onClick={(e) => handleViewService(service, e)}
                                className="view-btn bg-white text-black text-[9px] sm:text-[10px] font-black px-4 sm:px-6 py-2 sm:py-2.5 rounded-full shadow-2xl transform translate-y-3 group-hover:translate-y-0 transition-all duration-300 hover:bg-black hover:text-white"
                              >
                                VIEW
                              </button>
                            </div>

                            {/* Selection checkbox */}
                            {selectionMode && (
                              <div
                                className={`absolute top-2 left-2 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center border-2 transition-all cursor-pointer z-10 ${
                                  isSelected
                                    ? 'bg-[#984e5c] border-[#984e5c]'
                                    : 'bg-white/80 border-gray-400 hover:border-[#984e5c]'
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleService(service);
                                }}
                              >
                                {isSelected && (
                                  <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                            )}
                          </div>

                          <h3 className="text-[10px] sm:text-[11px] font-bold uppercase tracking-tight text-gray-800 text-center truncate w-full mb-1 px-2">
                            {service.name}
                          </h3>
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            <span className="text-[#984e5c] font-black text-[10px] sm:text-[11px]">
                              &#8369;{Number(service.price).toLocaleString()}
                            </span>
                            {formatDuration(service.duration_minutes) && (
                              <>
                                <span className="text-gray-200 text-[10px]">|</span>
                                <span className="text-gray-500 text-[9px] font-bold uppercase tracking-tighter">
                                  {formatDuration(service.duration_minutes)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Right arrow */}
                  <button
                    onClick={() => scrollRight(category)}
                    className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-100 ml-2 sm:ml-4 text-sm"
                  >
                    &#x2192;
                  </button>
                </div>
              </div>
            </div>
          ))
        )}

        {/* See All Treatments */}
        {isLoggedIn && (
          <div className="mt-8 sm:mt-12 text-center">
            <button
              onClick={() => navigate('/treatments')}
              className="px-6 sm:px-8 py-3 bg-[#984e5c] text-white rounded-full font-bold text-sm hover:bg-[#7a3f4d] active:scale-95 transition-all"
            >
              See All Treatments
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedService && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md p-0 sm:p-4 animate-in fade-in duration-300">
          {/* Bottom sheet on mobile, centered modal on sm+ */}
          <div className="w-full sm:max-w-sm rounded-t-[2rem] sm:rounded-[3rem] bg-white shadow-2xl overflow-hidden animate-in slide-in-from-bottom sm:zoom-in duration-300">
            <div className="relative h-48 sm:h-56 bg-gray-100">
              {selectedService.image && (
                <img src={selectedService.image} alt="" className="w-full h-full object-cover" />
              )}
              <button
                onClick={() => setSelectedService(null)}
                className="absolute top-4 right-4 sm:top-6 sm:right-6 w-9 h-9 sm:w-10 sm:h-10 bg-black/20 backdrop-blur-md rounded-full text-white flex items-center justify-center hover:bg-black/40 transition-all text-sm"
              >
                ✕
              </button>
              {/* Drag handle for mobile */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-white/40 rounded-full sm:hidden" />
            </div>
            <div className="p-6 sm:p-10">
              <span className="text-[10px] font-black text-[#984e5c] uppercase tracking-[0.2em]">
                {selectedService.category_name}
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900 uppercase mt-2 tracking-tighter leading-none">
                {selectedService.name}
              </h2>
              <div className="flex justify-between items-center my-4 sm:my-6 py-4 sm:py-5 border-y border-gray-50">
                <span className="text-xl sm:text-2xl font-black text-gray-800 tracking-tighter">
                  &#8369;{Number(selectedService.price).toLocaleString()}
                </span>
                {formatDuration(selectedService.duration_minutes) && (
                  <span className="text-gray-400 text-[10px] font-black uppercase tracking-widest bg-gray-50 px-3 py-1 rounded-full">
                    {formatDuration(selectedService.duration_minutes)}
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-xs italic mb-6 sm:mb-8 leading-relaxed">
                {selectedService.description || 'A signature wellness experience curated for total rejuvenation.'}
              </p>
              <button
                className="w-full py-3.5 sm:py-4 rounded-2xl bg-black text-white font-black uppercase text-[10px] tracking-[0.2em] hover:bg-[#984e5c] active:scale-95 transition-all shadow-lg"
                onClick={() => {
                  if (!selectionMode) setSelectionMode(true);
                  setSelectedService(null);
                }}
              >
                Add to Booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky bottom bar on mobile when services are selected */}
      {selectionMode && selectedServices.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 sm:hidden bg-white border-t border-gray-200 px-4 py-3 flex items-center gap-3 shadow-lg">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-bold truncate">
              {selectedServices.length} service{selectedServices.length > 1 ? 's' : ''} selected
            </p>
            <p className="text-base font-black text-[#984e5c]">&#8369;{totalPrice.toLocaleString()}</p>
          </div>
          <button
            onClick={handleBookClick}
            className="px-5 py-2.5 bg-[#984e5c] text-white rounded-full font-black text-[10px] uppercase tracking-[0.2em] hover:bg-black active:scale-95 transition-all"
          >
            Book Now
          </button>
        </div>
      )}

      {/* Booking Modal */}
      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        selectedServices={selectedServices}
        services={services}
        onServicesChange={setSelectedServices}
        defaultBranchId={selectedBranchId}
        user={user}
        isLoggedIn={isLoggedIn}
        onBookingComplete={handleBookingComplete}
      />
    </div>
  );
};

export default Services;
